import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, Car, Megaphone, Newspaper, TrendingUp, TrendingDown } from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import AppLayout from '../layout/AppLayout';
import { COLORS } from '../../utils/constants';

const C = COLORS;
const CHART_COLORS = ['#4F46E5', '#06B6D4', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981'];

// Fallback data for when API has no records yet
const defaultBarData = [
  { month: 'Jan', users: 340, ads: 120 }, { month: 'Feb', users: 420, ads: 180 },
  { month: 'Mar', users: 510, ads: 210 }, { month: 'Apr', users: 480, ads: 250 },
  { month: 'May', users: 620, ads: 310 }, { month: 'Jun', users: 590, ads: 280 },
];
const defaultPieData = [
  { name: 'Active', value: 1240 }, { name: 'Inactive', value: 320 },
  { name: 'Pending', value: 180 }, { name: 'Suspended', value: 60 },
];
const defaultAreaData = [
  { day: 'Mon', visits: 2400 }, { day: 'Tue', visits: 1398 }, { day: 'Wed', visits: 4800 },
  { day: 'Thu', visits: 3908 }, { day: 'Fri', visits: 4800 }, { day: 'Sat', visits: 3200 }, { day: 'Sun', visits: 2100 },
];

const Card = ({ children, style }) => <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, padding: 24, ...style }}>{children}</div>;

const DashboardPage = () => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    dashboardAPI.summary().then(r => setSummary(r.data)).catch(() => {});
  }, []);

  const icons = [<Users size={22} />, <Car size={22} />, <Megaphone size={22} />, <Newspaper size={22} />];
  const colors = [{ c: C.primary, bg: C.primaryGhost }, { c: '#0891B2', bg: '#ECFEFF' }, { c: '#7C3AED', bg: '#F5F3FF' }, { c: '#EA580C', bg: '#FFF7ED' }];

  const cards = summary?.cards || [
    { label: 'Total Users', value: 0, change: '+0%' },
    { label: 'Vehicles', value: 0, change: '+0%' },
    { label: 'Active Ads', value: 0, change: '+0%' },
    { label: 'News Articles', value: 0, change: '+0%' },
  ];

  return (
    <AppLayout title="Dashboard">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {cards.map((c, i) => {
            const up = c.change?.startsWith('+');
            return (
              <Card key={i} style={{ padding: 22, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -10, right: -10, width: 70, height: 70, borderRadius: '50%', background: colors[i].bg, opacity: 0.6 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, color: C.g500, fontWeight: 500 }}>{c.label}</p>
                    <p style={{ margin: '8px 0 6px', fontSize: 30, fontWeight: 800, color: C.g900, letterSpacing: -1 }}>{c.value?.toLocaleString()}</p>
                    <span style={{ fontSize: 12, fontWeight: 600, color: up ? C.success : C.danger, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {c.change}
                    </span>
                  </div>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: colors[i].bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors[i].c }}>{icons[i]}</div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          <Card>
            <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: C.g900 }}>Growth Overview</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={defaultBarData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke={C.g200} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: C.g500 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: C.g500 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${C.g200}`, fontSize: 13 }} />
                <Bar dataKey="users" fill={C.primary} radius={[6, 6, 0, 0]} />
                <Bar dataKey="ads" fill="#06B6D4" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: C.g900 }}>User Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={defaultPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" stroke="none">
                  {defaultPieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 8 }}>
              {defaultPieData.map((d, i) => (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.g600 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: CHART_COLORS[i] }} /> {d.name}
                </span>
              ))}
            </div>
          </Card>
        </div>

        {/* Area Chart */}
        <Card>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: C.g900 }}>Weekly Traffic</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={defaultAreaData}>
              <defs><linearGradient id="aG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.primary} stopOpacity={0.2} /><stop offset="100%" stopColor={C.primary} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.g200} vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: C.g500 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: C.g500 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Area type="monotone" dataKey="visits" stroke={C.primary} strokeWidth={2.5} fill="url(#aG)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </AppLayout>
  );
};

export default DashboardPage;
